import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Receipt as ReceiptIcon,
  Call as CallIcon,
  GetApp as GetAppIcon,
  Devices as DevicesIcon,
  EventNote as EventNoteIcon,
  MonetizationOn as MonetizationOnIcon,
  CalendarMonth as CalendarMonthIcon,
  CalendarToday as CalendarTodayIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axiosInstance from '../utlis/axiosInstance';
import emailjs from '@emailjs/browser';

interface Transaction {
  id: string;
  order_id: string;
  razorpay_order_id?: string;
  amount: number;
  currency: string;
  status: string;
  payment_status?: string;
  payment_id?: string;
  paid_at?: string;
  receipt?: string | null;
  created_at: string;
  updated_at: string;
  razorpay_signature?: string | null;
  user?: number;
  plan?: number;
  device?: number | null;
  metadata?: Record<string, unknown>;
  error_code?: string | null;
  error_description?: string | null;
  customer_details?: {
    name: string;
    email: string;
    phone: string;
  };
  notes?: string;
  // Convenience fields extracted from metadata for table display
  imie?: string;
  vehicle_name?: string;
  customer_name?: string;
  customer_phone?: string;
  mdn?: string;
}

interface TransactionDetails extends Transaction {
  payment_method?: string;
  payment_capture?: boolean;
  attempts?: number;
}

const TransactionManagement: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionDetails | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [enteredOtp, setEnteredOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');

  const mapTransaction = (item: any): Transaction => ({
    id: String(item?.id ?? ''),
    order_id: item?.razorpay_order_id ?? item?.order_id ?? '',
    razorpay_order_id: item?.razorpay_order_id ?? item?.order_id ?? '',
    amount: Number(item?.amount ?? 0),
    currency: item?.currency ?? 'INR',
    status: String(item?.status ?? item?.payment_status ?? 'unknown'),
    payment_status: item?.payment_status,
    payment_id: item?.razorpay_payment_id ?? item?.payment_id ?? null,
    paid_at: item?.paid_at,
    receipt: item?.receipt ?? null,
    created_at: item?.created_at ?? '',
    updated_at: item?.updated_at ?? '',
    razorpay_signature: item?.razorpay_signature ?? null,
    user: item?.user,
    plan: item?.plan,
    device: item?.device ?? null,
    metadata: item?.metadata,
    error_code: item?.error_code ?? null,
    error_description: item?.error_description ?? null,
    // These may not exist in the new payload; keep placeholders for UI compatibility
    customer_details: item?.customer_details,
    notes: item?.notes ?? (item?.metadata ? JSON.stringify(item.metadata) : undefined),
    imie: String(item?.metadata?.IMIE ?? item?.metadata?.imie ?? ''),
    vehicle_name: String(item?.metadata?.vehicle_name ?? item?.metadata?.vehicle ?? ''),
    customer_name: String(item?.metadata?.customer_name ?? item?.metadata?.customer ?? ''),
    customer_phone: String(item?.metadata?.customer_phone ?? item?.metadata?.phone ?? ''),
    mdn: String(item?.metadata?.MDN ?? item?.metadata?.mdn ?? ''),
  });

  // Fetch all transactions
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/gpstracker/razorpayorderhandler/');
      const data = response?.data?.responseData ?? [];
      setTransactions(Array.isArray(data) ? data.map(mapTransaction) : []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch transaction details by order ID
  const fetchTransactionDetails = async (orderId: string) => {
    try {
      setDetailsLoading(true);
      const response = await axiosInstance.get(`/gpstracker/razorpayorderhandler/${orderId}`);
      const payload = response?.data?.responseData;
      setSelectedTransaction(payload ? mapTransaction(payload) : null);
      setDetailsDialogOpen(true);
    } catch (err: any) {
      console.error('Error fetching transaction details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    emailjs.init('_DDifojD8EZvzFU20');
    fetchTransactions();
    // Get user email from localStorage or session
    const email = localStorage.getItem('savedEmail') || '';
    setUserEmail(email);
  }, []);

  // Generate invoice number: DDMMYY + transaction ID (padded to 3 digits)
  const generateInvoiceNumber = (transactionId: string, createdAt: string) => {
    const date = new Date(createdAt);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const seq = String(Number(transactionId) % 1000).padStart(3, '0');
    return `${day}${month}${year}${seq}`;
  };

  // Filter transactions based on search term and status
  const filteredTransactions = transactions.filter(transaction => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = !term || (
      String(transaction?.order_id ?? '').toLowerCase().includes(term) ||
      String(transaction?.payment_id ?? '').toLowerCase().includes(term) ||
      String(transaction?.payment_status ?? '').toLowerCase().includes(term) ||
      String(transaction?.status ?? '').toLowerCase().includes(term) ||
      String(transaction?.customer_details?.name ?? '').toLowerCase().includes(term) ||
      String(transaction?.customer_details?.email ?? '').toLowerCase().includes(term) ||
      String(transaction?.imie ?? '').toLowerCase().includes(term) ||
      String(transaction?.vehicle_name ?? '').toLowerCase().includes(term) ||
      String(transaction?.customer_name ?? '').toLowerCase().includes(term) ||
      String(transaction?.customer_phone ?? '').toLowerCase().includes(term)
    );

    const matchesStatus = statusFilter === 'all' || String(transaction.status ?? '').toLowerCase() === statusFilter;

    // Date range filter (From/To) applied live
    let matchesDate = true;
    if (exportFrom) {
      const from = new Date(exportFrom);
      matchesDate = !!transaction.created_at && new Date(transaction.created_at) >= from;
    }
    if (matchesDate && exportTo) {
      const to = new Date(exportTo);
      to.setHours(23,59,59,999);
      matchesDate = !!transaction.created_at && new Date(transaction.created_at) <= to;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'captured':
      case 'completed':
        return 'success';
      case 'pending':
      case 'created':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Razorpay amounts are in paise
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isSameDay = (d1?: Date | null, d2?: Date | null) => {
    if (!d1 || !d2) return false;
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  };

  const isAdmin = () => {
    return userEmail === 'acumenadmin@gmail.com' || userEmail === 'acumenretail@gmail.com';
  };

  const isWithinWeek = (dateString?: string) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    return diff >= 0 && diff <= 7 * 24 * 60 * 60 * 1000;
  };

  const isWithinYear = (dateString?: string) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    return date.getFullYear() === now.getFullYear();
  };

  const generateOTP = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendOTPEmail = async (otp: string) => {
    setOtpSending(true);
    setOtpError('');

    try {
      const templateParams = {
        to_email: 'acumentrack27@gmail.com',
        otp_code: otp,
        from_name: 'Acumen Tracking System',
        message: `Your OTP for exporting transaction data is: ${otp}. This OTP is valid for 5 minutes.`,
      };

      await emailjs.send(
        'service_txzkllb',
        'template_8v4fo88',
        templateParams
      );

      setOtpSuccess('OTP sent successfully to Admin email!');
      setTimeout(() => setOtpSuccess(''), 5000);
      return true;
    } catch (error: any) {
      console.error('EmailJS Error:', error);
      setOtpError(error.text || error.message || 'Failed to send OTP. Please try again.');
      return false;
    } finally {
      setOtpSending(false);
    }
  };

  const handleOpenOtpDialog = async () => {
    if (filteredTransactions.length === 0) {
      setError('No transactions to export.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const otp = generateOTP();
    setGeneratedOtp(otp);
    setEnteredOtp('');
    setOtpDialogOpen(true);

    await sendOTPEmail(otp);
  };

  const handleVerifyOtpAndExport = () => {
    if (enteredOtp === generatedOtp) {
      setOtpDialogOpen(false);
      setOtpError('');
      setEnteredOtp('');
      exportFilteredToCSV();
    } else {
      setOtpError('Invalid OTP. Please check and try again.');
    }
  };

  const totalDevices = useMemo(() => {
    const s = new Set<number | string>();
    transactions.forEach((t) => { if (t.device !== undefined && t.device !== null) s.add(t.device); });
    return s.size;
  }, [transactions]);

  const todayTransactions = useMemo(() => {
    const now = new Date();
    return transactions.filter(t => t.created_at && isSameDay(new Date(t.created_at), now));
  }, [transactions]);

  const todayAddedCount = todayTransactions.length;
  const todayRevenue = todayTransactions.reduce((acc, t) => acc + (Number(t.amount) || 0), 0);

  const thisWeekRevenue = useMemo(() => {
    return transactions
      .filter(t => t.created_at && isWithinWeek(t.created_at))
      .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
  }, [transactions]);

  const thisYearRevenue = useMemo(() => {
    return transactions
      .filter(t => t.created_at && isWithinYear(t.created_at))
      .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
  }, [transactions]);

  const thisMonthRevenue = useMemo(() => {
    const now = new Date();
    return transactions
      .filter((t) => {
        if (!t.created_at) return false;
        const d = new Date(t.created_at);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      })
      .reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
  }, [transactions]);

  const plan1200Count = useMemo(() => {
    return transactions.filter(t => t.plan === 1200 && (t.status?.toLowerCase() === 'captured' || t.status?.toLowerCase() === 'completed' || t.payment_status?.toLowerCase() === 'completed')).length;
  }, [transactions]);

  const plan999Count = useMemo(() => {
    return transactions.filter(t => t.plan === 999 && (t.status?.toLowerCase() === 'captured' || t.status?.toLowerCase() === 'completed' || t.payment_status?.toLowerCase() === 'completed')).length;
  }, [transactions]);

  const thisMonthDevices = useMemo(() => {
    const s = new Set<number | string>();
    const now = new Date();
    transactions.forEach((t) => {
      if (t.device !== undefined && t.device !== null && t.created_at) {
        const d = new Date(t.created_at);
        if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) s.add(t.device);
      }
    });
    return s.size;
  }, [transactions]);

  const thisYearDevices = useMemo(() => {
    const s = new Set<number | string>();
    const now = new Date();
    transactions.forEach((t) => {
      if (t.device !== undefined && t.device !== null && t.created_at) {
        const d = new Date(t.created_at);
        if (d.getFullYear() === now.getFullYear()) s.add(t.device);
      }
    });
    return s.size;
  }, [transactions]);

  const exportFilteredToCSV = () => {
    const from = exportFrom ? new Date(exportFrom) : null;
    const to = exportTo ? new Date(exportTo) : null;
    let items = filteredTransactions.slice();
    if (from) items = items.filter(i => new Date(i.created_at) >= from);
    if (to) { const toEnd = new Date(to); toEnd.setHours(23,59,59,999); items = items.filter(i => new Date(i.created_at) <= toEnd); }

    const header = ['IMIE','Vehicle','Customer','Customer Phone','Amount','Payment Status','Payment ID','Paid At','Created At'];
    const rows = items.map(r => [
      r.imie || '',
      r.vehicle_name || '',
      r.customer_name || r.customer_details?.name || '',
      r.customer_phone || r.customer_details?.phone || '',
      formatCurrency(r.amount, r.currency),
      r.payment_status || r.status || '',
      r.payment_id || '',
      formatDate(r.paid_at),
      formatDate(r.created_at),
    ]);

    const csvContent = [header, ...rows].map(r => r.map(cell => `"${String(cell).replace(/"/g,'""') }"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_export_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleViewDetails = (orderId: string) => {
    fetchTransactionDetails(orderId);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedTransaction(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ReceiptIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
          <Typography variant="h6" component="h6">
            Transaction Management
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Analytics Cards (compact and colorful) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'nowrap' }}>
        <Box sx={{ flex: 1 }}>
          <Card sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
            <CardContent sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Devices</Typography>
                <Typography variant="h6">{totalDevices}</Typography>
              </Box>
              <DevicesIcon fontSize="large" sx={{ opacity: 0.9 }} />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Card sx={{ backgroundColor: 'success.main', color: 'success.contrastText' }}>
            <CardContent sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Today Added</Typography>
                <Typography variant="h6">{todayAddedCount}</Typography>
              </Box>
              <EventNoteIcon fontSize="large" sx={{ opacity: 0.9 }} />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Card sx={{ backgroundColor: 'warning.main', color: 'warning.contrastText' }}>
            <CardContent sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>This Month Devices</Typography>
                <Typography variant="h6">{thisMonthDevices}</Typography>
              </Box>
              <CalendarMonthIcon fontSize="large" sx={{ opacity: 0.9 }} />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Card sx={{ backgroundColor: 'secondary.main', color: 'secondary.contrastText' }}>
            <CardContent sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>This Year Devices</Typography>
                <Typography variant="h6">{thisYearDevices}</Typography>
              </Box>
              <CalendarTodayIcon fontSize="large" sx={{ opacity: 0.9 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Admin-only Analytics Row */}
      {isAdmin() && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'nowrap' }}>
          <Box sx={{ flex: 1 }}>
            <Card sx={{ backgroundColor: 'info.main', color: 'info.contrastText' }}>
              <CardContent sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Today Revenue</Typography>
                  <Typography variant="h6">{formatCurrency(todayRevenue, 'INR')}</Typography>
                </Box>
                <MonetizationOnIcon fontSize="large" sx={{ opacity: 0.9 }} />
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card sx={{ backgroundColor: 'warning.main', color: 'warning.contrastText' }}>
              <CardContent sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>This Week Revenue</Typography>
                  <Typography variant="h6">{formatCurrency(thisWeekRevenue, 'INR')}</Typography>
                </Box>
                <MonetizationOnIcon fontSize="large" sx={{ opacity: 0.9 }} />
              </CardContent>
            </Card>
          </Box>


          <Box sx={{ flex: 1 }}>
            <Card sx={{ backgroundColor: '#f59e0b', color: '#ffffff' }}>
              <CardContent sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>This Month Revenue</Typography>
                  <Typography variant="h6">{formatCurrency(thisMonthRevenue, 'INR')}</Typography>
                </Box>
                <MonetizationOnIcon fontSize="large" sx={{ opacity: 0.9 }} />
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card sx={{ backgroundColor: 'error.main', color: 'error.contrastText' }}>
              <CardContent sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>This Year Revenue</Typography>
                  <Typography variant="h6">{formatCurrency(thisYearRevenue, 'INR')}</Typography>
                </Box>
                <MonetizationOnIcon fontSize="large" sx={{ opacity: 0.9 }} />
              </CardContent>
            </Card>
          </Box>

        </Box>
      )}

      {/* (Export controls moved into Filters row for a single-line compact header) */}

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 40%', minWidth: 240 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by IMIE, vehicle, customer or payment ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 360px' }}>
              <TextField
                size="small"
                label="From"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={exportFrom}
                onChange={(e) => setExportFrom(e.target.value)}
              />
              <TextField
                size="small"
                label="To"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={exportTo}
                onChange={(e) => setExportTo(e.target.value)}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 220px' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="captured">Captured</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {isAdmin() && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    onClick={handleOpenOtpDialog}
                    disabled={otpSending || filteredTransactions.length === 0}
                  >
                    Export
                  </Button>
                )}
                <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTransactions}>Refresh</Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

   

      {/* Transactions Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Transactions ({filteredTransactions.length})
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Invoice #</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>MDN</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>IMIE</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Vehicle</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Customer</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Customer Phone</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Amount</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Payment Status</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Payment ID</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Paid At</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No transactions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((transaction) => (
                    <TableRow
                      key={transaction.id}
                      hover
                      onClick={() => handleViewDetails(transaction.razorpay_order_id || transaction.order_id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="medium">{generateInvoiceNumber(transaction.id, transaction.created_at)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2">{transaction.mdn || ''}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2" fontFamily="monospace">{transaction.imie || ''}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2">{transaction.vehicle_name || ''}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2" fontWeight="medium">{transaction.customer_name || transaction.customer_details?.name || ''}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        {(transaction.customer_phone || transaction.customer_details?.phone) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            
                            <Typography variant="body2">{transaction.customer_phone || transaction.customer_details?.phone || ''}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">{''}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2" fontWeight="medium">{formatCurrency(transaction.amount, transaction.currency)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Chip label={transaction.payment_status || transaction.status || 'N/A'} color={getStatusColor(transaction.status || '') as any} size="small" />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2" fontFamily="monospace">{transaction.payment_id || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2">{formatDate(transaction.paid_at)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2">{formatDate(transaction.created_at)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* OTP Verification Dialog */}
      <Dialog
        open={otpDialogOpen}
        onClose={() => !otpSending && setOtpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0) 100%)',
            }}
          >
            <SecurityIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              OTP Verification Required
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              An OTP has been sent to <strong>admin's email</strong>. Please enter the 6-digit code to proceed with export.
            </Alert>

            {otpSending && (
              <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Sending OTP...
                </Typography>
              </Box>
            )}

            {otpSuccess && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {otpSuccess}
              </Alert>
            )}

            {otpError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {otpError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Enter OTP"
              value={enteredOtp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setEnteredOtp(value);
                setOtpError('');
              }}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: {
                  fontSize: '24px',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  fontWeight: 600,
                },
              }}
              sx={{ '& input': { textAlign: 'center' } }}
              disabled={otpSending}
              autoFocus
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                OTP valid for 5 minutes
              </Typography>
              <Button
                size="small"
                onClick={handleOpenOtpDialog}
                disabled={otpSending}
                sx={{ textTransform: 'none' }}
              >
                Resend OTP
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setOtpDialogOpen(false);
              setOtpError('');
              setEnteredOtp('');
            }}
            disabled={otpSending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleVerifyOtpAndExport}
            disabled={enteredOtp.length !== 6 || otpSending}
            startIcon={<CheckCircleIcon />}
          >
            Verify & Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Transaction Details
          {selectedTransaction && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Razorpay Order ID: {selectedTransaction.razorpay_order_id || selectedTransaction.order_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedTransaction ? (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment Status
                  </Typography>
                  <Chip
                    label={selectedTransaction.payment_status || selectedTransaction.status}
                    color={getStatusColor(selectedTransaction.status) as any}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Order Status
                  </Typography>
                  <Typography variant="body2">
                    {selectedTransaction.status}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment ID
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {selectedTransaction.payment_id || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Paid At
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedTransaction.paid_at)}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body2">
                    {selectedTransaction.payment_method || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedTransaction.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Updated At
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedTransaction.updated_at)}
                  </Typography>
                </Box>
                {(selectedTransaction.user || selectedTransaction.plan || selectedTransaction.device !== undefined) && (
                  <>
                    <Box sx={{ flex: '1 1 100%', mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Meta
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        User
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.user ?? 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Plan
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.plan ?? 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Device
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.device ?? 'N/A'}
                      </Typography>
                    </Box>
                  </>
                )}
                {selectedTransaction.customer_details && (
                  <>
                    <Box sx={{ flex: '1 1 100%', mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Customer Details
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.customer_details.name}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.customer_details.email}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">
                        {selectedTransaction.customer_details.phone}
                      </Typography>
                    </Box>
                  </>
                )}
                {selectedTransaction.notes && (
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {selectedTransaction.notes}
                    </Typography>
                  </Box>
                )}
                {selectedTransaction.receipt && (
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Receipt
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {selectedTransaction.receipt}
                    </Typography>
                  </Box>
                )}
                {(selectedTransaction.error_code || selectedTransaction.error_description) && (
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Error Details
                    </Typography>
                    <Typography variant="body2">
                      {selectedTransaction.error_code || 'N/A'} - {selectedTransaction.error_description || 'N/A'}
                    </Typography>
                  </Box>
                )}
                {selectedTransaction.metadata && (
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Metadata
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedTransaction.metadata, null, 2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionManagement;
